import { unifiedMergeView } from '@codemirror/merge';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { forwardRef, useMemo } from 'react';
import { CodeEditor, CodeEditorProps } from '../code-editor';
import { PrometheusConfig } from './codemirror/client/prometheus';
import { usePromQLExtensions } from './use-promql-extensions';

export interface PromQLEditorProps extends Omit<CodeEditorProps, 'extensions' | 'tabBehavior'> {
  queryApiEndpoint?: string;
  enableLinter?: boolean;
  timeRange?: PrometheusConfig['timeRange'];

  /**
   * If `previousValue` is provided, user will see a merge/diff view of the previous value and current value.
   */
  previousValue?: string;

  /**
   * Enables autocompletion of built-in variables like $__rate_interval, $__interval, $__range
   * in duration contexts (e.g., inside square brackets like `[5m]` or `[$__rate_interval]`).
   */
  enableBuiltInVariables?: boolean;
}

export const PromQLEditor = forwardRef<ReactCodeMirrorRef, PromQLEditorProps>(function PromQLEditor(
  { queryApiEndpoint, enableLinter, timeRange, previousValue, enableBuiltInVariables, ...rest }: PromQLEditorProps,
  ref
) {
  const promqlExtensions = usePromQLExtensions({
    queryApiEndpoint,
    enableLinter,
    timeRange,
    enableBuiltInVariables,
  });

  // diff view stays here so the shared hook remains pure promql semantics — host
  // editors that render their own diff (e.g. unified QueryEditor.Input) can opt out
  const extensions = useMemo(() => {
    if (!previousValue) return promqlExtensions;
    return [
      ...promqlExtensions,
      unifiedMergeView({
        allowInlineDiffs: false,
        mergeControls: false,
        original: previousValue,
      }),
    ];
  }, [promqlExtensions, previousValue]);

  return (
    <CodeEditor placeholder="Enter a PromQL query" {...rest} extensions={extensions} ref={ref} tabBehavior="focus" />
  );
});
